import { useState, useEffect, useMemo } from 'react';
import {
    Box, Card, CardContent, Typography, Grid2, Chip, IconButton, Button, Dialog, DialogTitle,
    DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, TextField, Stack, Avatar, Divider,
    FormControlLabel, Switch
} from '@mui/material';
import {
    LocalShipping, Phone, LocationOn, Person, AttachMoney, Edit, CheckCircle, Cancel, Refresh, Print
} from '@mui/icons-material';
import Navbar from '../setup/Navbar';
import axios from 'axios';
import { useSelector } from 'react-redux';
import '../../services/GlobalVariables';
import { getPrintBehaviorSettings } from '../../utils/printBehaviorSettings';

/**
 * Delivery Dashboard - Kanban Board Style
 * Shows active delivery orders grouped by status.
 * Includes mobile-app order filtering and direct KOT print action.
 * Allows rider assignment and status updates.
 */

const DeliveryDashboard = () => {
    const [dashboard, setDashboard] = useState({
        activeOrders: [],
        riders: [],
        zones: []
    });
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [selectedRider, setSelectedRider] = useState('');
    const [newStatus, setNewStatus] = useState('');
    const [statusReason, setStatusReason] = useState('');
    const [showCustomerAppOnly, setShowCustomerAppOnly] = useState(true);
    const { user } = useSelector((state) => state.auth || {});

    // Fetch dashboard data
    const fetchDashboard = async () => {
        try {
            const response = await axios.get(`${global.SET_API_URL}delivery/dashboard`);
            if (response.data.success) {
                setDashboard({
                    activeOrders: response.data.data.activeOrders || [],
                    riders: response.data.data.riders || [],
                    zones: response.data.data.zones || []
                });
            }
        } catch (error) {
            console.error('Error fetching delivery dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboard();
        // Auto-refresh every 30 seconds
        const interval = setInterval(fetchDashboard, 30000);
        return () => clearInterval(interval);
    }, []);

    const filteredOrders = useMemo(() => {
        if (!showCustomerAppOnly) return dashboard.activeOrders;
        return dashboard.activeOrders.filter((order) => order.orderSource === 'customer_app');
    }, [dashboard.activeOrders, showCustomerAppOnly]);

    // Group orders by delivery status
    const ordersByStatus = {
        pending: filteredOrders.filter((o) => o.deliveryStatus === 'pending'),
        preparing: filteredOrders.filter((o) => o.deliveryStatus === 'preparing'),
        ready: filteredOrders.filter((o) => o.deliveryStatus === 'ready'),
        out_for_delivery: filteredOrders.filter((o) => o.deliveryStatus === 'out_for_delivery')
    };

    // Status colors
    const statusColors = {
        pending: '#9c27b0',
        preparing: '#ff9800',
        ready: '#2196f3',
        out_for_delivery: '#4caf50',
        delivered: '#757575',
        cancelled: '#f44336'
    };

    const sourceLabelMap = {
        customer_app: 'Customer App',
        foodpanda: 'Foodpanda',
        manual_delivery: 'Manual Delivery'
    };

    const sourceColorMap = {
        customer_app: 'success',
        foodpanda: 'warning',
        manual_delivery: 'default'
    };

    // Assign rider to order
    const handleAssignRider = async () => {
        try {
            const response = await axios.post(`${global.SET_API_URL}delivery/assignRider`, {
                orderID: selectedOrder.orderID,
                riderID: selectedRider,
                assignedBy: 1 // TODO: Get from auth
            });

            if (response.data.success) {
                alert('Rider assigned successfully!');
                setAssignDialogOpen(false);
                fetchDashboard();
            }
        } catch (error) {
            console.error('Error assigning rider:', error);
            alert('Failed to assign rider');
        }
    };

    // Update order status
    const handleUpdateStatus = async () => {
        try {
            const response = await axios.post(`${global.SET_API_URL}delivery/updateStatus`, {
                orderID: selectedOrder.orderID,
                newStatus: newStatus,
                changedBy: 1, // TODO: Get from auth
                reason: statusReason
            });

            if (response.data.success) {
                alert('Status updated successfully!');
                setStatusDialogOpen(false);
                fetchDashboard();
            }
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Failed to update status');
        }
    };

    // Print pending KOT for delivery order
    const handlePrintKOT = async (order) => {
        const printSettings = getPrintBehaviorSettings();

        if (!printSettings.kot) {
            alert('KOT printing is disabled from Print Behavior Settings.');
            return;
        }

        try {
            const response = await axios.post(`${global.SET_API_URL}kot/printKOT`, {
                orderID: order.orderID,
                userID: user?.userID || 1,
                printOptions: {
                    kot: printSettings.kot,
                    completeOrder: printSettings.completeOrder,
                    finalBill: printSettings.finalBill
                }
            });

            if (!response.data?.success) {
                alert(`KOT printing failed: ${response.data?.message || 'Unknown error'}`);
                return;
            }

            let message = response.data.message || 'KOT processed successfully.';

            // Move pending order to preparing right after successful KOT action
            if (order.deliveryStatus === 'pending') {
                await axios.post(`${global.SET_API_URL}delivery/updateStatus`, {
                    orderID: order.orderID,
                    newStatus: 'preparing',
                    changedBy: user?.userID || 1,
                    reason: 'KOT printed from delivery dashboard'
                });
                message += '\nStatus moved to PREPARING.';
            }

            alert(message);
            fetchDashboard();
        } catch (error) {
            console.error('Error printing KOT:', error);
            alert(`Failed to print KOT: ${error.response?.data?.message || error.message}`);
        }
    };

    // Format currency
    const currency = (amount) => `Rs. ${Number(amount).toLocaleString('en-PK', { minimumFractionDigits: 2 })}`;

    // Order Card Component
    const OrderCard = ({ order }) => (
        <Card 
            sx={{ 
                mb: 2, 
                border: `2px solid ${statusColors[order.deliveryStatus]}`,
                '&:hover': { boxShadow: 6 }
            }}
        >
            <CardContent sx={{ p: 2 }}>
                {/* Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6" fontWeight={600}>
                        Order #{order.orderID}
                    </Typography>
                    <Chip 
                        label={`${order.minutesSinceOrder} min`} 
                        size="small" 
                        color={order.minutesSinceOrder > 30 ? 'error' : 'default'}
                    />
                </Stack>

                <Divider sx={{ my: 1 }} />

                {/* Customer Info */}
                <Stack spacing={0.5} mb={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Person fontSize="small" color="action" />
                        <Typography variant="body2">{order.customerName || 'N/A'}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Phone fontSize="small" color="action" />
                        <Typography variant="body2">{order.customerPhone}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="flex-start" spacing={1}>
                        <LocationOn fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                            {order.customerAddress || 'No address'}
                        </Typography>
                    </Stack>
                </Stack>

                {/* Zone & Amount */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Chip 
                        label={order.deliveryZone || 'No Zone'} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                    />
                    <Typography variant="h6" color="primary" fontWeight={700}>
                        {currency(order.grandTotal)}
                    </Typography>
                </Stack>

                <Stack direction="row" spacing={1} mb={1} flexWrap="wrap" useFlexGap>
                    <Chip
                        size="small"
                        label={sourceLabelMap[order.orderSource] || 'Delivery'}
                        color={sourceColorMap[order.orderSource] || 'default'}
                    />
                    <Chip
                        size="small"
                        label={Number(order.pendingKotItems || 0) > 0 ? `KOT Pending: ${order.pendingKotItems}` : 'KOT Printed'}
                        color={Number(order.pendingKotItems || 0) > 0 ? 'warning' : 'success'}
                        variant={Number(order.pendingKotItems || 0) > 0 ? 'filled' : 'outlined'}
                    />
                </Stack>

                {/* Payment Method */}
                <Chip 
                    label={order.paymentMethod === 'cod' ? `COD - Collect ${currency(order.amountToCollect)}` : 'PREPAID'} 
                    size="small" 
                    color={order.paymentMethod === 'cod' ? 'warning' : 'success'}
                    sx={{ mb: 1 }}
                />

                {/* Rider Info */}
                {order.riderName ? (
                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                        <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                            {order.riderName.charAt(0)}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500}>
                            {order.riderName}
                        </Typography>
                    </Stack>
                ) : (
                    <Typography variant="body2" color="text.secondary" mb={1}>
                        No rider assigned
                    </Typography>
                )}

                {/* Action Buttons */}
                {order.deliveryStatus === 'pending' ? (
                    <Stack direction="row" spacing={1} mt={2}>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<Print />}
                            onClick={() => handlePrintKOT(order)}
                            fullWidth
                            color="warning"
                        >
                            Print KOT
                        </Button>
                    </Stack>
                ) : null}

                <Stack direction="row" spacing={1} mt={order.deliveryStatus === 'pending' ? 1 : 2}>
                    <Button 
                        size="small" 
                        variant="outlined" 
                        startIcon={<LocalShipping />}
                        onClick={() => {
                            setSelectedOrder(order);
                            setSelectedRider(order.riderID || '');
                            setAssignDialogOpen(true);
                        }}
                        fullWidth
                    >
                        {order.riderName ? 'Change Rider' : 'Assign Rider'}
                    </Button>
                    <Button 
                        size="small" 
                        variant="contained" 
                        startIcon={<Edit />}
                        onClick={() => {
                            setSelectedOrder(order);
                            setNewStatus(order.deliveryStatus);
                            setStatusReason('');
                            setStatusDialogOpen(true);
                        }}
                        fullWidth
                    >
                        Update Status
                    </Button>
                </Stack>
            </CardContent>
        </Card>
    );

    // Status Column Component
    const StatusColumn = ({ title, status, orders, icon, color }) => (
        <Box>
            <Card sx={{ bgcolor: color, color: 'white', mb: 2 }}>
                <CardContent sx={{ p: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1}>
                            {icon}
                            <Typography variant="h6" fontWeight={600}>
                                {title}
                            </Typography>
                        </Stack>
                        <Chip 
                            label={orders.length} 
                            sx={{ bgcolor: 'white', color: color, fontWeight: 700 }}
                            size="small"
                        />
                    </Stack>
                </CardContent>
            </Card>

            <Box sx={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto', pr: 1 }}>
                {orders.length === 0 ? (
                    <Card>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary" align="center">
                                No orders in this status
                            </Typography>
                        </CardContent>
                    </Card>
                ) : (
                    orders.map(order => <OrderCard key={order.orderID} order={order} />)
                )}
            </Box>
        </Box>
    );

    if (loading) {
        return (
            <>
                <Navbar />
                <Box sx={{ p: 3 }}>
                    <Typography>Loading dashboard...</Typography>
                </Box>
            </>
        );
    }

    return (
        <>
            <Navbar />
            <Box sx={{ p: 2 }}>
                {/* Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h4" fontWeight={700}>
                        🏍️ Delivery Dashboard
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showCustomerAppOnly}
                                    onChange={(e) => setShowCustomerAppOnly(e.target.checked)}
                                />
                            }
                            label="Customer App Only"
                        />
                        <Button
                            variant="outlined"
                            startIcon={<Refresh />}
                            onClick={fetchDashboard}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Stack>

                {/* Stats */}
                <Grid2 container spacing={2} mb={3}>
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
                            <CardContent>
                                <Typography variant="h4" fontWeight={700}>
                                    {filteredOrders.length}
                                </Typography>
                                <Typography variant="body2">Active Orders</Typography>
                            </CardContent>
                        </Card>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
                            <CardContent>
                                <Typography variant="h4" fontWeight={700}>
                                    {dashboard.riders.filter(r => r.activeDeliveries > 0).length}
                                </Typography>
                                <Typography variant="body2">Active Riders</Typography>
                            </CardContent>
                        </Card>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={{ bgcolor: 'warning.main', color: 'white' }}>
                            <CardContent>
                                <Typography variant="h4" fontWeight={700}>
                                    {ordersByStatus.pending.length}
                                </Typography>
                                <Typography variant="body2">Pending</Typography>
                            </CardContent>
                        </Card>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={{ bgcolor: 'info.main', color: 'white' }}>
                            <CardContent>
                                <Typography variant="h4" fontWeight={700}>
                                    {ordersByStatus.out_for_delivery.length}
                                </Typography>
                                <Typography variant="body2">On Route</Typography>
                            </CardContent>
                        </Card>
                    </Grid2>
                </Grid2>

                {/* Kanban Board */}
                <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <StatusColumn
                            title="Pending"
                            status="pending"
                            orders={ordersByStatus.pending}
                            icon={<Cancel />}
                            color="#9c27b0"
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <StatusColumn 
                            title="Preparing"
                            status="preparing"
                            orders={ordersByStatus.preparing}
                            icon={<AttachMoney />}
                            color="#ff9800"
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <StatusColumn 
                            title="Ready"
                            status="ready"
                            orders={ordersByStatus.ready}
                            icon={<CheckCircle />}
                            color="#2196f3"
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <StatusColumn 
                            title="Out for Delivery"
                            status="out_for_delivery"
                            orders={ordersByStatus.out_for_delivery}
                            icon={<LocalShipping />}
                            color="#4caf50"
                        />
                    </Grid2>
                </Grid2>

                {/* Assign Rider Dialog */}
                <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Assign Rider - Order #{selectedOrder?.orderID}</DialogTitle>
                    <DialogContent>
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel>Select Rider</InputLabel>
                            <Select
                                value={selectedRider}
                                onChange={(e) => setSelectedRider(e.target.value)}
                                label="Select Rider"
                            >
                                {dashboard.riders.map(rider => (
                                    <MenuItem key={rider.riderID} value={rider.riderID}>
                                        {rider.riderName} ({rider.activeDeliveries} active)
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
                        <Button 
                            variant="contained" 
                            onClick={handleAssignRider}
                            disabled={!selectedRider}
                        >
                            Assign
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Update Status Dialog */}
                <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Update Status - Order #{selectedOrder?.orderID}</DialogTitle>
                    <DialogContent>
                        <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
                            <InputLabel>New Status</InputLabel>
                            <Select
                                value={newStatus}
                                onChange={(e) => setNewStatus(e.target.value)}
                                label="New Status"
                            >
                                <MenuItem value="preparing">Preparing</MenuItem>
                                <MenuItem value="ready">Ready</MenuItem>
                                <MenuItem value="out_for_delivery">Out for Delivery</MenuItem>
                                <MenuItem value="delivered">Delivered</MenuItem>
                                <MenuItem value="cancelled">Cancelled</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Reason (optional)"
                            multiline
                            rows={3}
                            value={statusReason}
                            onChange={(e) => setStatusReason(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
                        <Button 
                            variant="contained" 
                            onClick={handleUpdateStatus}
                            disabled={!newStatus}
                        >
                            Update
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
};

export default DeliveryDashboard;
